/**
 * I livelli del dato energetico, dichiarati **una volta sola**.
 *
 * Prima di questo file i quattro nomi erano ridichiarati in sei posti
 * indipendenti — `curve-slots.ts`, la tabella `RANK` di `pod-breakdown.ts`,
 * `MetricBasis` nel registro metriche, `HistoricalSource`, `DemoLayer`, e i
 * `COLORS`/`LABELS` della chart — e i sei erano andati in deriva: due ordini di
 * priorità diversi (il livello 2 e il 3 scambiati fra la curva e le sorgenti
 * grezze), tre vocabolari per la stessa cosa (`reale`/`meter`/`meter_reader`,
 * `stimata`/`estimated`/`blend`) e un livello — la curva tipica — che esisteva
 * come colore nella legenda ma non come livello.
 *
 * L'ordine qui sotto è **la** lista: vince il rango più basso che ha un valore
 * sullo slot.
 *
 *   1 GSE      il dato certificato, non si discute;
 *   2 Reale    quello che è stato misurato — dal contatore o ricostruito da una
 *              fattura, e fra i due il rilevato sovrascrive la bolletta;
 *   3 Stimato  la forma tipica riscalata sulle quantità reali vicine;
 *   4 Tipico   la sola forma tipica, che non lascia mai buchi ed è per questo
 *              che sta in fondo.
 */

export const LAYERS = [
	{
		rank: 1,
		name: 'gse',
		label: 'GSE',
		hint: 'Solo il dato certificato dal GSE',
	},
	{
		rank: 2,
		name: 'reale',
		label: 'Reale',
		hint: 'Misura del contatore, o ricostruita da bolletta',
	},
	{
		rank: 3,
		name: 'stimato',
		label: 'Stimato',
		hint: 'La curva tipica riscalata sui valori reali',
	},
	{
		rank: 4,
		name: 'tipico',
		label: 'Tipico',
		hint: 'La sola curva tipica, senza dati reali',
	},
] as const;

export type LayerName = (typeof LAYERS)[number]['name'];

/** I nomi nell'ordine di priorità. */
export const LAYER_NAMES: readonly LayerName[] = LAYERS.map((l) => l.name);

/** Da nome a rango. Chi ordina usa questo, chi disegna usa il nome. */
export const LAYER_RANK = Object.fromEntries(LAYERS.map((l) => [l.name, l.rank])) as Record<
	LayerName,
	number
>;

/** Da rango a nome: è così che `meter_day_curve.layer` si legge. */
export const LAYER_BY_RANK = Object.fromEntries(LAYERS.map((l) => [l.rank, l.name])) as Record<
	number,
	LayerName
>;

export const LAYER_LABEL = Object.fromEntries(LAYERS.map((l) => [l.name, l.label])) as Record<
	LayerName,
	string
>;

export const LAYER_HINT = Object.fromEntries(LAYERS.map((l) => [l.name, l.hint])) as Record<
	LayerName,
	string
>;

/**
 * Non è un livello: è l'**esito** della priorità, il valore che è stato scelto
 * slot per slot. Tenerlo come riga accanto ai livelli invece che come colonna
 * evita di ripetere lo stesso numero su ognuno di essi.
 */
export const RECONCILED = 'riconciliato';
export type ReconciledName = typeof RECONCILED;

/** Un livello, oppure l'esito della riconciliazione. */
export type LayerOrReconciled = LayerName | ReconciledName;

/**
 * Sotto-provenienza di un singolo slot, in `meter_day_curve.origin`.
 *
 * Serve a due cose che il livello da solo non sa dire. La prima è la regola di
 * sovrascrittura del livello 2: una misura del contatore rimpiazza un valore
 * ricostruito da fattura, mai il contrario, e senza sapere da dove viene lo slot
 * la regola non è applicabile a una ricostruzione successiva. La seconda è
 * l'onestà del livello 1: il GSE certifica **ore**, quindi i quarti d'ora sono
 * una ripartizione piatta e il dato deve dirlo.
 */
export const ORIGIN = {
	/** Misura del contatore: broker MQTT, provider, o import. */
	rilevato: 1,
	/** Ricostruito da una fattura. */
	bolletta: 2,
	/** Certificato GSE, nativo quartorario. */
	gse: 3,
	/** Certificato GSE orario, ripartito in quattro quarti uguali. */
	gseFlat: 4,
	/** Tipica riscalata su ancore reali. */
	stima: 5,
	/** Curva tipica pura. */
	tipica: 6,
	/**
	 * Stima calibrata sull'irraggiamento misurato.
	 *
	 * Si distingue da {@link ORIGIN.stima} perché non è la stessa cosa detta
	 * meglio: la stima nuda prende la forma dal profilo GSE — una campana media
	 * di stagione, uguale il giorno sereno e quello coperto — mentre questa la
	 * prende dal GHI orario di quella località in quel giorno. Sapere quale dei
	 * due si sta guardando è la differenza fra «l'impianto ha reso poco» e «c'era
	 * un temporale», ed è una domanda che si fa a posteriori, quando il meteo di
	 * quel giorno non è più recuperabile dallo slot.
	 */
	stimaMeteo: 7,
	/**
	 * Bolletta vera caricata da un socio: solo il totale del periodo di
	 * fatturazione, ripartito piatto sulle sue ore.
	 *
	 * Si distingue da {@link ORIGIN.bolletta} per lo stesso motivo per cui
	 * {@link ORIGIN.gseFlat} si distingue da {@link ORIGIN.gse}: una bolletta
	 * residenziale non certifica una forma oraria, solo un totale di periodo —
	 * spesso un mese — e spalmarlo uniforme senza dirlo farebbe sembrare
	 * misurata una forma che nessuno ha misurato. A parità di livello perde
	 * contro `bolletta` nella fusione (codice più alto), perché una bolletta con
	 * una forma nota è comunque più informativa di una piatta.
	 */
	bollettaFlat: 8,
} as const;

export type OriginCode = (typeof ORIGIN)[keyof typeof ORIGIN];

/** Etichette delle provenienze che la UI mostra come sfumatura di un livello. */
export const ORIGIN_LABEL: Record<OriginCode, string> = {
	[ORIGIN.rilevato]: 'rilevato',
	[ORIGIN.bolletta]: 'da bolletta',
	[ORIGIN.gse]: 'certificato',
	[ORIGIN.gseFlat]: 'certificato orario',
	[ORIGIN.stima]: 'stima',
	[ORIGIN.tipica]: 'tipica',
	[ORIGIN.stimaMeteo]: 'stima su meteo',
	[ORIGIN.bollettaFlat]: 'da bolletta (periodo)',
};

/** Le provenienze ammesse per ciascun livello. */
export const LAYER_ORIGINS: Record<LayerName, readonly OriginCode[]> = {
	gse: [ORIGIN.gse, ORIGIN.gseFlat],
	reale: [ORIGIN.rilevato, ORIGIN.bolletta, ORIGIN.bollettaFlat],
	stimato: [ORIGIN.stima, ORIGIN.stimaMeteo],
	tipico: [ORIGIN.tipica],
};

/** Vero se `v` è uno dei quattro livelli (e non `riconciliato` o altro). */
export function isLayerName(v: string): v is LayerName {
	return (LAYER_NAMES as readonly string[]).includes(v);
}
